import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { URLS } from '~/components-shop/Urls';
import { sanitizeHtml } from '~/components-shop/utils/sanitizeHtml';

interface ImageSize {
  url: string;
  width: number;
  height: number;
}

interface ThumbnailGroup {
  [mediaQuery: string]: ImageSize;
}

interface DosageFormImageResponse {
  uniquePacksizeId: string;
  oNumber: string;
  thumbnails: {
    [groupName: string]: ThumbnailGroup;
  };
}

interface CartImageProps {
  oNumberOrUniquePacksizeId?: string;
  mode?: 'json' | 'picture';
}

// Component to fetch and display product images from the dosageform-image endpoint
export const CartImage = ({ oNumberOrUniquePacksizeId, mode = 'picture' }: CartImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    if (!oNumberOrUniquePacksizeId) {
      setIsLoading(false);
      return;
    }

    const fetchImage = async () => {
      try {
        const baseUrl = URLS.dosageFormImage;
        const url =
          mode === 'json'
            ? `${baseUrl}/json?oNumber=${oNumberOrUniquePacksizeId}`
            : `${baseUrl}/html?oNumber=${oNumberOrUniquePacksizeId}`;

        const response = await fetch(url);
        if (response.ok && isMounted) {
          if (mode === 'json') {
            const data: DosageFormImageResponse = await response.json();
            // Find the smallest image across all thumbnail groups
            let smallestImage: ImageSize | null = null;
            let smallestWidth = Infinity;

            for (const groupName in data.thumbnails) {
              const group = data.thumbnails[groupName];
              for (const mediaQuery in group) {
                const image = group[mediaQuery];
                if (image.width < smallestWidth) {
                  smallestWidth = image.width;
                  smallestImage = image;
                }
              }
            }

            if (smallestImage && isMounted) {
              setImageUrl(smallestImage.url);
            }
          } else {
            // picture mode - sanitize and insert HTML
            const html = await response.text();
            if (containerRef.current && isMounted) {
              containerRef.current.innerHTML = sanitizeHtml(html);
            }
          }
        }
      } catch (error) {
        console.error('[CartImage] Failed to fetch image:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [oNumberOrUniquePacksizeId, mode]);

  if (mode === 'json') {
    return imageUrl ? (
      <img src={imageUrl} alt="" loading="lazy" className={isLoading ? 'opacity-50' : ''} />
    ) : (
      <div className={isLoading ? 'opacity-50' : ''} />
    );
  }

  return <div ref={containerRef} className={isLoading ? 'opacity-50' : ''} />;
};
